import { DatePicker, Select, Space, Tag, Typography } from 'antd'
import { AuthLog } from 'common/api/api'
import { authLogApi } from 'common/api/entitiesApi.tsx'
import { ListCard } from 'common/components/ListCard'
import { AccountSelector } from 'common/components/reloadable/AccountSelector.tsx'
import { AgencySelector } from 'common/components/reloadable/AgencySelector.tsx'
import { ListFilters, useAppContext } from 'common/context/AppContext'
import { initListFilters, useListFilters } from 'common/util/useListFilters'
import { apiCall } from 'common/util/Util'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

const { RangePicker } = DatePicker

const DATE_FORMAT = 'YYYY-MM-DD'
const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const ellipsisText = (value: any, maxWidth: number) => (
  <Typography.Text
    style={{ maxWidth, display: 'inline-block' }}
    ellipsis={{ tooltip: value || undefined }}
  >
    {value || '-'}
  </Typography.Text>
)

const AuthLogList = () => {
  const appContext = useAppContext()

  const defaultEnd = dayjs().endOf('day')
  const defaultStart = defaultEnd.subtract(6, 'day').startOf('day')

  const [filters, setFilters] = useState<ListFilters>(initListFilters({ startInterval: defaultStart, endInterval: defaultEnd }))

  const { setFilter } = useListFilters(setFilters)

  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<any>()

  const dateRangeValue = useMemo((): [Dayjs, Dayjs] | undefined => {
    if (!filters.startInterval || !filters.endInterval) return undefined
    return [filters.startInterval as unknown as Dayjs, filters.endInterval as unknown as Dayjs]
  }, [filters.startInterval, filters.endInterval])

  const loadData = () => {

    apiCall(
      setLoading,
      appContext,
      () =>
        authLogApi.authLogList(
          filters.startInterval?.format(DATE_FORMAT).toString(),
          filters.endInterval?.format(DATE_FORMAT).toString(),
          filters.accountId,
          filters.agencyId,
          filters.interfaceId,
          {
            query: { page: filters.page, size: filters.pageSize },
          },
        ),
      (data) => setListData(data),
    )
  }

  useEffect(() => {
    loadData()
  }, [filters])

  return (
    <ListCard<AuthLog>
      loading={loading}
      title="Auth Logs"
      filters={filters}
      setFilters={setFilters}
      listData={listData}
      loadData={loadData}
      additionalButtons={[
        {
          name: 'Refresh',
          onClick: loadData,
        },
      ]}
      columns={[
        {
          title: 'Date/Time',
          width: 180,
          render: (_: any, r: AuthLog) => dayjs(r.time).format(DATETIME_FORMAT),
        },
        // {
        //   title: 'Event',
        //   dataIndex: 'eventType',
        //   width: 200,
        // },
        // {
        //   title: 'Failure reason',
        //   dataIndex: 'failureReason',
        //   width: 220,
        // },
        {
          title: 'Interface',
          dataIndex: 'interfaceId',
          width: 140,
        },
        {
          title: 'Agency',
          dataIndex: 'agencyId',
          width: 110,
          render: (_: any, r: AuthLog) => ellipsisText((r.agencyId ? r.agencyName ? `${r.agencyId} ${r.agencyName}` : r.agencyId : '-' ), 140),
        },
        {
          title: 'Account',
          dataIndex: 'accountId',
          width: 110,
          render: (_: any, r: AuthLog) => ellipsisText((r.accountId ? r.accountName ? `${r.accountId} ${r.accountName}` : r.accountId : '-' ), 140),
        },
        {
          title: 'Login As From',
          dataIndex: 'actorAccountId',
          width: 110,
          render: (_: any, r: AuthLog) => ellipsisText((r.actorAccountId ? r.actorAccountName ? `${r.actorAccountId} ${r.actorAccountName}` : r.actorAccountId : '-' ), 140),
        },
        {
          title: 'Result',
          width: 120,
          render: (_: any, r: AuthLog) => (r.success ? <Tag>SUCCESS</Tag> : <Tag>{r.failureReason}</Tag>),
        },
        {
          title: 'Client IP',
          dataIndex: 'clientIp',
          width: 140,
        },
        {
          title: 'User-Agent',
          dataIndex: 'userAgent',
          width: 320,
          render: (v: string) => <Typography.Text ellipsis={{ tooltip: v }}>{v}</Typography.Text>,
        },
        {
          title: 'XFF IP',
          dataIndex: 'xffRaw',
          width: 240,
          render: (v: string) => <Typography.Text ellipsis={{ tooltip: v }}>{v}</Typography.Text>,
        },
        {
          title: 'Host',
          dataIndex: 'host',
          width: 220,
        },
        {
          title: 'Origin',
          dataIndex: 'origin',
          width: 220,
        },
        {
          title: 'Referer',
          dataIndex: 'referer',
          width: 220,
        },
        {
          title: 'Login',
          dataIndex: 'login',
          width: 220,
        },
      ]}
    >
      <Space wrap>
        <RangePicker
          value={dateRangeValue as any}
          format={DATE_FORMAT}
          allowClear
          onChange={(val) => {
            if (!val || !val[0] || !val[1]) {
              setFilter('startInterval' as any, undefined)
              setFilter('endInterval' as any, undefined)
              return
            }
            setFilter('startInterval' as any, val[0].startOf('day'))
            setFilter('endInterval' as any, val[1].endOf('day'))
          }}
        />

        <Select
          placeholder="Interface ID"
          allowClear
          showSearch
          style={{ width: 180 }}
          value={filters.interfaceId}
          onChange={(v) => setFilter('interfaceId' as any, v || undefined)}
          options={[
            { value: 'admin', label: 'admin' },
            { value: 'agency', label: 'agency' },
            { value: 'advertiser', label: 'advertiser' },
          ]}
        />

        <AgencySelector
          placeholder="Filter by Agency"
          style={{ minWidth: '200px' }}
          value={filters.agencyId}
          onChange={(value: number) => setFilter('agencyId', value)}
        />

        <AccountSelector
          placeholder="Filter by Account"
          style={{ minWidth: '200px' }}
          value={filters.accountId}
          onChange={(value: number) => setFilter('accountId', value)}
        />
      </Space>
    </ListCard>
  )
}

export default AuthLogList
